import {
  CameraIcon,
  VideoCameraIcon,
  MicrophoneIcon,
  ComputerDesktopIcon,
} from '@heroicons/react/24/outline';

type GearItem = {
  name: string;
  subtitle: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  points: string[];
  image: string;
  badge?: string;
};

const gear: GearItem[] = [
  {
    name: 'Sony A7 IV',
    subtitle: 'Full-frame hybrid for photo + video',
    icon: CameraIcon,
    points: [
      'Go-to body for trips, low-light scenes, and portraits',
      '4K video, great dynamic range, fast autofocus',
      'Used for: cinematic B-roll, interviews, travel portraits',
    ],
    badge: 'Main Camera',
    image:
      'https://images.unsplash.com/photo-1516035069371-29a1b244cc32?w=400&h=400&fit=crop',
  },
  {
    name: 'GoPro HERO 12',
    subtitle: 'Action + waterproof compact',
    icon: VideoCameraIcon,
    points: [
      'Super wide POV and HyperSmooth stabilization',
      'Perfect for water, hiking, biking, and timelapses',
      'Used for: on-the-go moments and hands-free capture',
    ],
    badge: 'Action',
    image:
      'https://images.unsplash.com/photo-1526170375885-4d8ecf77b99f?w=400&h=400&fit=crop',
  },
  {
    name: 'DJI Mavic 3',
    subtitle: 'Travel drone for aerial storytelling',
    icon: VideoCameraIcon,
    points: [
      'Stable 4K footage, great battery life, reliable tracking',
      'Used for: establishing shots, coastal fly-overs',
      'We always fly responsibly and follow regulations',
    ],
    badge: 'Aerial',
    image:
      'https://images.unsplash.com/photo-1473968512647-3e447244af8f?w=400&h=400&fit=crop',
  },
  {
    name: 'Portrait Lens',
    subtitle: '50mm / 85mm, wide aperture',
    icon: CameraIcon,
    points: [
      'Creamy background blur for people and food',
      'Great in low-light cafés and night markets',
      'Used for: storytelling portraits and detail shots',
    ],
    badge: 'Portraits',
    image:
      'https://images.unsplash.com/photo-1495707902641-75cac588d2e9?w=400&h=400&fit=crop',
  },
  {
    name: 'DJI Mic 2',
    subtitle: 'Wireless microphones with wind protection',
    icon: MicrophoneIcon,
    points: [
      'Dual transmitters for two speakers/interviews',
      'Reliable range and compact charging case',
      'Used for: vlogs, voice-overs, and walk-and-talks',
    ],
    badge: 'Audio',
    image:
      'https://images.unsplash.com/photo-1598488035139-bdbb2231ce04?w=400&h=400&fit=crop',
  },
  {
    name: 'Final Cut Pro',
    subtitle: 'Mac-based editor for travel videos',
    icon: ComputerDesktopIcon,
    points: [
      'Magnetic timeline speeds up rough cuts',
      'Built-in stabilization, speed ramping, color grading',
      'Used for: YouTube episodes, reels, and highlight films',
    ],
    badge: 'Editing',
    image:
      'https://images.unsplash.com/photo-1574717024653-61fd2cf4d44d?w=400&h=400&fit=crop',
  },
];

export default function GearSection() {
  return (
    <section className="py-16 bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-2xl font-bold text-slate-900 mb-3">
            Our Photography & Filmmaking Gear
          </h2>
          <p className="text-slate-600 max-w-2xl mx-auto">
            The tools we rely on to capture adventures, sound great, and keep
            our workflow fast on the road.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {gear.map(g => (
            <div
              key={g.name}
              className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm hover:shadow-md transition-shadow"
            >
              {/* Image */}
              <div className="relative h-48 overflow-hidden bg-slate-100">
                <img
                  src={g.image}
                  alt={g.name}
                  className="w-full h-full object-cover"
                />
                {g.badge && (
                  <span className="absolute top-3 left-3 text-xs font-medium px-2.5 py-1 rounded-full bg-slate-900 text-white">
                    {g.badge}
                  </span>
                )}
              </div>

              {/* Content */}
              <div className="p-5">
                <div className="flex items-start gap-3 mb-3">
                  <g.icon className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-semibold text-slate-900">{g.name}</h3>
                    <p className="text-sm text-slate-500">{g.subtitle}</p>
                  </div>
                </div>
                <ul className="space-y-1.5 text-sm text-slate-600">
                  {g.points.map(p => (
                    <li key={p} className="flex items-start">
                      <span className="text-blue-600 mr-2">•</span>
                      {p}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>

        <p className="mt-10 text-center text-sm text-slate-500">
          We balance portability with quality so we can create consistently—from
          ocean splashes to mountaintop sunrises.
        </p>
      </div>
    </section>
  );
}
